import React, { useContext, useEffect, useState } from "react"
import { Div, Text } from "atomize"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faChevronRight, faPen } from "@fortawesome/free-solid-svg-icons"
import { DateTime } from "luxon"
import { theme } from "../settings/config"
import { modalContext } from "../providers/modal.provider"
import { profileContext } from "../providers/profile.provider"
import { schoolContext } from "../providers/school.provider"
import { sharedDataContext } from "../providers/sharedData.provider"
import { canEditLesson, getLessonColor } from "../utils/func.utils"
import { DASH } from "../atoms/DashedOutline"
import { finishedLabel, latestAwaitingNotes } from "../utils/lessonNotes.utils"

const endsToday = lesson => {
	const end = lesson?.lessonEnd?.toDate?.()
	return Boolean(end) && DateTime.fromJSDate(end).hasSame(DateTime.now(), "day")
}

// Delle lezioni di un giorno, quella che chiede com'è andata: l'ultima finita
// senza note, tra quelle che si possono modificare (vedi lessonNotes.utils),
// con la scritta di quando è finita. L'orologio gira solo se qualcuna finisce
// oggi, una volta al minuto allo scoccare del minuto: è lì che "4 minuti fa"
// diventa "5", e che una lezione appena finita prende il biglietto senza
// toccare niente.
export function useJustFinished(lessons) {
	const { coach } = useContext(profileContext)
	const { selectedSchool } = useContext(schoolContext)
	const candidates = (lessons ?? []).filter(
		lesson => endsToday(lesson) && canEditLesson(coach, selectedSchool, lesson),
	)
	const isTicking = candidates.length > 0
	const [now, setNow] = useState(() => DateTime.now())

	useEffect(() => {
		if (!isTicking) return
		let interval
		const tick = () => setNow(DateTime.now())
		tick()
		const timeout = setTimeout(
			() => {
				tick()
				interval = setInterval(tick, 60 * 1000)
			},
			60 * 1000 - (Date.now() % (60 * 1000)),
		)
		return () => {
			clearTimeout(timeout)
			clearInterval(interval)
		}
	}, [isTicking])

	const latest = latestAwaitingNotes(candidates, now)
	return latest ? { lessonID: latest.lessonID, label: finishedLabel(latest.lessonEnd, now) } : null
}

// Il biglietto (#214): in fondo alla scheda, staccato da una riga
// tratteggiata da bordo a bordo, la parte che chiede le note. È dello stesso
// fondo della scheda, e l'arancione è tutto nel tondo della matita e nella
// domanda: abbastanza per vederlo, una scheda sola in tutta la giornata.
// Si tocca tutto, e la scheda non si apre.
//
// La riga ha l'inchiostro della lezione, attenuato: un grigio fisso nello
// scuro finiva della stessa tinta del fondo e spariva, così invece stacca da
// qualunque tipologia, in tutti e due i temi.
const STUB_LINE_OPACITY = 0.4

export function JustFinishedStub({ lessonData, label }) {
	const Modal = useContext(modalContext)
	const { getLessonType } = useContext(sharedDataContext)
	const lineColor = getLessonColor(getLessonType(lessonData.lessonType), true)

	return (
		<button
			type="button"
			onClick={event => {
				event.stopPropagation()
				Modal.open("lessonDiary", lessonData)
			}}
			style={{
				display: "block",
				width: "100%",
				marginTop: "0.75rem",
				padding: 0,
				background: "transparent",
				border: "none",
				font: "inherit",
				textAlign: "left",
				cursor: "pointer",
			}}>
			<svg
				aria-hidden="true"
				focusable="false"
				style={{ display: "block", width: "100%", height: 2, overflow: "visible" }}>
				<line
					x1="0"
					y1="1"
					x2="100%"
					y2="1"
					style={{
						stroke: lineColor,
						strokeWidth: 2,
						strokeDasharray: DASH,
						strokeOpacity: STUB_LINE_OPACITY,
					}}
				/>
			</svg>
			<Div d="flex" align="center" p="0.85rem 1rem 0.95rem" style={{ gap: "0.75rem" }}>
				<Div
					d="flex"
					align="center"
					justify="center"
					w="2.25rem"
					h="2.25rem"
					rounded="circle"
					bg={theme.colors.primary}
					style={{ flexShrink: 0 }}>
					<FontAwesomeIcon icon={faPen} color={theme.colors.white} size="sm" />
				</Div>
				<Div flexGrow="1" style={{ minWidth: 0 }}>
					<Text textSize="caption" textColor={theme.colors.text_light}>
						{label}
					</Text>
					<Text textWeight="600" textColor={theme.colors.primary}>
						Vuoi aggiungere due note?
					</Text>
				</Div>
				<FontAwesomeIcon icon={faChevronRight} color={theme.colors.primary} />
			</Div>
		</button>
	)
}
